import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, Copy, KeyRound, Loader2, RefreshCw, Trash2 } from "lucide-react";
import Button from "@/components/Button";
import apiClient from "@/lib/api-client";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { useConfirm } from "@/contexts/ConfirmContext";
import { useGlobalToast } from "@/contexts/ToastContext";
import { usePermissions } from "@/hooks/usePermissions";

/**
 * Integración con TPV Lucrapp (1-nov-2026, solo restaurantes): el restaurante
 * genera una clave de API y la pega en su TPV, que con ella lee las reservas
 * del día de esta sede.
 *
 * La clave completa solo existe en la respuesta de generarla: después el
 * servidor guarda su huella y aquí solo se ve el principio. Por eso se enseña
 * una vez, en el aviso verde, y si se pierde hay que generar otra.
 */

interface TpvKey {
  keyPrefix: string;
  createdAt: string;
  lastUsedAt: string | null;
}

interface TpvSpend {
  enabled: boolean;
  enabledAt: string | null;
}

/** Valoración que el restaurante debe completar antes de activar el gasto. */
const SPEND_ASSESSMENT_URL = "https://reservamanager.com/valoracion-historial-gasto";

/** La dirección que se pega en el TPV: dominio propio, para no enseñar dónde está alojado el servidor. */
const TPV_API_URL = "https://api.reservamanager.com";

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });

/** Un dato para copiar: dirección, sede o la clave recién generada. */
function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const { showToast } = useGlobalToast();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast("No se ha podido copiar. Selecciónalo y cópialo a mano.", "error");
    }
  };

  return (
    <div>
      <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
      <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg pl-3 pr-1 py-1">
        <code className="flex-1 min-w-0 text-xs md:text-sm text-gray-800 break-all">{value}</code>
        <button
          type="button"
          onClick={handleCopy}
          aria-label={`Copiar ${label.toLowerCase()}`}
          className="flex-shrink-0 p-2 rounded-md text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
        >
          {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export default function TpvIntegrationCard({
  locationId,
  locationName,
}: {
  locationId: string;
  locationName: string;
}) {
  const { can } = usePermissions();
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();
  const canManage = can("integraciones:gestionar");

  const [key, setKey] = useState<TpvKey | null>(null);
  const [spend, setSpend] = useState<TpvSpend>({ enabled: false, enabledAt: null });
  const [savingSpend, setSavingSpend] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!canManage) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await apiClient.get("/integrations/tpv", { params: { locationId } });
      setKey(data.data.key);
      setSpend(data.data.spend);
    } catch (error) {
      setLoadError(extractApiErrorMessage(error, "No se ha podido cargar la integración"));
    } finally {
      setLoading(false);
    }
  }, [canManage, locationId]);

  useEffect(() => {
    // Al cambiar de sede no puede quedar a la vista la clave de la anterior.
    setNewApiKey(null);
    setKey(null);
    void load();
  }, [load]);

  const handleGenerate = async () => {
    if (key) {
      const ok = await confirm({
        title: "¿Generar una clave nueva?",
        message:
          "La clave actual dejará de funcionar al momento y el TPV dejará de ver las reservas hasta que pegues la nueva.",
        confirmText: "Generar clave nueva",
        danger: true,
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const { data } = await apiClient.post("/integrations/tpv/key", { locationId });
      setKey(data.data.key);
      setNewApiKey(data.data.apiKey);
    } catch (error) {
      showToast(extractApiErrorMessage(error, "No se ha podido generar la clave"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSpendSwitch = async () => {
    const enabled = !spend.enabled;
    if (enabled) {
      const ok = await confirm({
        title: "¿Guardar el gasto en la ficha de cliente?",
        message:
          "Cada cuenta de una reserva guardará en la ficha del cliente el importe, la propina, los comensales y la fecha, durante 24 meses. Tu negocio es responsable de este uso: debes informar a tus clientes, atender a quien se oponga y cumplir la cláusula 5.5 de los Términos. Antes de activarlo, completa la valoración.",
        confirmText: "Activar",
      });
      if (!ok) return;
    }
    setSavingSpend(true);
    try {
      await apiClient.put("/integrations/tpv/spend", { locationId, enabled });
      await load();
      showToast(enabled ? "Gasto activado" : "Gasto desactivado", "success");
    } catch (error) {
      showToast(extractApiErrorMessage(error, "No se ha podido guardar el cambio"), "error");
    } finally {
      setSavingSpend(false);
    }
  };

  const handleRevoke = async () => {
    const ok = await confirm({
      title: "¿Revocar la clave?",
      message: "El TPV dejará de ver las reservas de esta sede hasta que generes otra clave.",
      confirmText: "Revocar",
      danger: true,
    });
    if (!ok) return;
    setSaving(true);
    try {
      await apiClient.delete("/integrations/tpv/key", { params: { locationId } });
      setKey(null);
      setNewApiKey(null);
      showToast("Clave revocada", "success");
    } catch (error) {
      showToast(extractApiErrorMessage(error, "No se ha podido revocar la clave"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">TPV Lucrapp</h2>
        <p className="text-sm text-gray-500 mt-1">
          Las reservas de <span className="font-medium">{locationName}</span> se ven dentro del
          TPV: quién viene, a qué hora, cuántas personas y en qué mesa.
        </p>
      </div>

      {!canManage ? (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-4">
          Necesitas el permiso de integraciones para generar la clave del TPV.
        </p>
      ) : loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      ) : loadError ? (
        <div className="flex flex-wrap items-center gap-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-4">
          <span className="flex-1 min-w-0">{loadError}</span>
          <Button variant="outline" size="sm" onClick={() => void load()}>
            Volver a intentarlo
          </Button>
        </div>
      ) : (
        <>
          {newApiKey && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-2 text-sm text-green-900">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p>
                  <span className="font-semibold">Copia la clave ahora.</span> Por seguridad no se
                  vuelve a mostrar. Si la pierdes, tendrás que generar otra.
                </p>
              </div>
              <CopyRow label="Clave de API" value={newApiKey} />
            </div>
          )}

          <div className="border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <KeyRound className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              {key ? (
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 break-all">
                    Clave activa: <code>{key.keyPrefix}…</code>
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Creada el {formatDateTime(key.createdAt)} ·{" "}
                    {key.lastUsedAt
                      ? `Último uso: ${formatDateTime(key.lastUsedAt)}`
                      : "Aún no se ha usado"}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-600">Esta sede todavía no tiene clave de API.</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={key ? "outline" : "primary"}
                size="sm"
                onClick={handleGenerate}
                disabled={saving}
                className="inline-flex items-center gap-2"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : key ? (
                  <RefreshCw className="w-4 h-4" />
                ) : (
                  <KeyRound className="w-4 h-4" />
                )}
                {key ? "Generar otra" : "Generar clave de API"}
              </Button>
              {key && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRevoke}
                  disabled={saving}
                  className="inline-flex items-center gap-2 text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                  Revocar
                </Button>
              )}
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-start gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900">
                  Guardar el gasto en la ficha de cliente
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Cada cuenta de una reserva guardará en la ficha del cliente el importe, la
                  propina, los comensales y la fecha, durante 24 meses. Tu negocio es responsable
                  de este uso: debes informar a tus clientes, atender a quien se oponga y cumplir
                  la cláusula 5.5 de los Términos.
                </p>
                <p className="text-sm mt-1">
                  <a
                    href={SPEND_ASSESSMENT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    Valoración que debes completar antes de activarlo
                  </a>
                </p>
                {spend.enabled && spend.enabledAt && (
                  <p className="text-xs text-gray-500 mt-1">
                    Activado el {formatDateTime(spend.enabledAt)}.
                  </p>
                )}
              </div>
              <Button
                variant={spend.enabled ? "outline" : "primary"}
                size="sm"
                onClick={handleSpendSwitch}
                disabled={savingSpend}
                className="inline-flex items-center gap-2 flex-shrink-0"
              >
                {savingSpend && <Loader2 className="w-4 h-4 animate-spin" />}
                {spend.enabled ? "Desactivar" : "Activar"}
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Cómo conectarlo</h3>
            <p className="text-sm text-gray-600">
              En TPV Lucrapp, entra en <span className="font-medium">Oficina → Integraciones →
              ReservaManager</span>, activa las reservas, pega estos datos y pulsa{" "}
              <span className="font-medium">Probar la conexión</span>.
            </p>
            <CopyRow label="Dirección de ReservaManager" value={TPV_API_URL} />
            <CopyRow label="Identificador de la sede" value={locationId} />
            <p className="text-xs text-gray-500">
              El token de integración es la clave de API de arriba. Para otro programa:{" "}
              <code className="break-all">
                GET {TPV_API_URL}/api/reservations?date=AAAA-MM-DD
              </code>{" "}
              con la cabecera <code>Authorization: Bearer &lt;clave&gt;</code>. La clave permite leer
              las reservas de esta sede, marcar llegada, sentada, no presentada y terminada desde el
              TPV, y guardar lo que costó cada cuenta. No sirve para crear ni cancelar reservas.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
